import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NutritionPlanConfigPanel = ({ clients, nutritionConfig, onUpdateConfig, onSavePlan, isSaving, mealDays }) => {
  const [errors, setErrors] = useState({});

  const totalFoods = mealDays?.reduce((acc, d) => acc + d?.meals?.reduce((a, m) => a + m?.foods?.length, 0), 0) || 0;
  const activeDays = mealDays?.filter(d => d?.meals?.some(m => m?.foods?.length > 0))?.length || 0;

  const totalCalories = mealDays?.reduce((acc, d) =>
    acc + d?.meals?.reduce((a, m) =>
      a + m?.foods?.reduce((f, item) => f + Math.round((item?.calories || 0) * (item?.quantity || 1)), 0), 0), 0) || 0;

  const validate = () => {
    const errs = {};
    if (!nutritionConfig?.name?.trim()) errs.name = 'Plan name is required';
    if (!nutritionConfig?.clientId) errs.clientId = 'Select a client';
    if (!nutritionConfig?.startDate) errs.startDate = 'Start date is required';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSave = () => {
    if (validate()) onSavePlan();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="Settings" size={16} className="text-accent" />
        </div>
        <h2 className="text-base font-bold text-foreground">Plan Settings</h2>
      </div>
      <div className="flex-1 overflow-y-auto space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-primary">{totalFoods}</p>
            <p className="text-xs text-muted-foreground">Food Items</p>
          </div>
          <div className="bg-secondary/5 border border-secondary/20 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-secondary">{activeDays}</p>
            <p className="text-xs text-muted-foreground">Active Days</p>
          </div>
        </div>

        {totalCalories > 0 && (
          <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-3 text-center">
            <p className="text-xl font-bold text-orange-400">{totalCalories?.toLocaleString()} kcal</p>
            <p className="text-xs text-muted-foreground">Avg Daily Calories</p>
          </div>
        )}

        {/* Client Selection */}
        <div>
          <label className="text-xs font-semibold text-foreground mb-1.5 block">Assign to Client *</label>
          <select
            value={nutritionConfig?.clientId || ''}
            onChange={e => onUpdateConfig('clientId', e?.target?.value)}
            className={`w-full px-3 py-2 rounded-xl border bg-background text-foreground text-sm ${errors?.clientId ? 'border-destructive' : 'border-border'}`}
          >
            <option value="">Select client...</option>
            {clients?.map(c => (
              <option key={c?.id} value={c?.id}>{c?.client?.full_name || c?.client?.email}</option>
            ))}
          </select>
          {errors?.clientId && <p className="text-xs text-destructive mt-1">{errors?.clientId}</p>}
        </div>

        {/* Plan Name */}
        <div>
          <label className="text-xs font-semibold text-foreground mb-1.5 block">Plan Name *</label>
          <Input
            value={nutritionConfig?.name || ''}
            onChange={e => onUpdateConfig('name', e?.target?.value)}
            placeholder="e.g. Fat Loss – 1800 kcal"
            className={`text-sm ${errors?.name ? 'border-destructive' : ''}`}
          />
          {errors?.name && <p className="text-xs text-destructive mt-1">{errors?.name}</p>}
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-semibold text-foreground mb-1.5 block">Description</label>
          <textarea
            value={nutritionConfig?.description || ''}
            onChange={e => onUpdateConfig('description', e?.target?.value)}
            placeholder="Plan overview and dietary goals..."
            rows={2}
            className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {/* Daily Macro Targets */}
        <div>
          <label className="text-xs font-semibold text-foreground mb-2 block flex items-center gap-1.5">
            <Icon name="Target" size={12} className="text-primary" />
            Daily Macro Targets
          </label>
          <div className="space-y-2">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Calories (kcal)</label>
              <Input
                type="number"
                value={nutritionConfig?.targetCalories || ''}
                onChange={e => onUpdateConfig('targetCalories', parseInt(e?.target?.value) || 0)}
                placeholder="e.g. 1800"
                className="text-sm"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-blue-400 mb-1 block">Protein (g)</label>
                <Input
                  type="number"
                  value={nutritionConfig?.targetProtein || ''}
                  onChange={e => onUpdateConfig('targetProtein', parseInt(e?.target?.value) || 0)}
                  placeholder="150"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-yellow-400 mb-1 block">Carbs (g)</label>
                <Input
                  type="number"
                  value={nutritionConfig?.targetCarbs || ''}
                  onChange={e => onUpdateConfig('targetCarbs', parseInt(e?.target?.value) || 0)}
                  placeholder="200"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-orange-400 mb-1 block">Fats (g)</label>
                <Input
                  type="number"
                  value={nutritionConfig?.targetFats || ''}
                  onChange={e => onUpdateConfig('targetFats', parseInt(e?.target?.value) || 0)}
                  placeholder="60"
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-foreground mb-1.5 block">Start Date *</label>
            <input
              type="date"
              value={nutritionConfig?.startDate || ''}
              onChange={e => onUpdateConfig('startDate', e?.target?.value)}
              className={`w-full px-3 py-2 rounded-xl border bg-background text-foreground text-sm ${errors?.startDate ? 'border-destructive' : 'border-border'}`}
            />
            {errors?.startDate && <p className="text-xs text-destructive mt-1">{errors?.startDate}</p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-foreground mb-1.5 block">End Date</label>
            <input
              type="date"
              value={nutritionConfig?.endDate || ''}
              onChange={e => onUpdateConfig('endDate', e?.target?.value)}
              className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
            />
          </div>
        </div>

        {/* Validation hints */}
        {totalFoods === 0 && (
          <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-xl">
            <Icon name="AlertTriangle" size={14} className="text-warning mt-0.5 flex-shrink-0" />
            <p className="text-xs text-warning">Add foods to at least one meal before saving</p>
          </div>
        )}

        {totalFoods > 0 && activeDays > 0 && (
          <div className="flex items-start gap-2 p-3 bg-success/10 border border-success/20 rounded-xl">
            <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
            <p className="text-xs text-success">{activeDays} days with {totalFoods} food items ready</p>
          </div>
        )}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <Button
          onClick={handleSave}
          disabled={isSaving || totalFoods === 0}
          className="w-full"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              Saving Plan...
            </>
          ) : (
            <>
              <Icon name="Save" size={16} className="mr-2" />
              Save & Assign Plan
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default NutritionPlanConfigPanel;
